import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import GeoContext from 'contexts/geoContext'
import type { ValueSectionItem } from './ValuePropositionItems'
import ValuePropositionItems from './ValuePropositionItems'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineUsdCircleIcon from 'LuxKit/Icons/line/LineUsdCircleIcon'
import { ATOL_REGIONS, NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function ValuePropositionBanner() {
  const { currentRegionCode } = useContext(GeoContext)
  const intl = useIntl()

  const values = useMemo(() => {
    const getValueSectionContent = (regionCode: string): Array<ValueSectionItem> => [
      {
        key: 'bestPrice',
        heading: intl.formatMessage({
          id: 'lCgksbjPMe',
          defaultMessage: 'Best price guarantee',
          description: 'Promotional text for the best price guarantee badge displayed on offer cards',
        }),
        body: intl.formatMessage({
          id: 'UX/D5ePDoH',
          defaultMessage: 'We negotiate the best prices so you don’t have to. If you find a better price, we’ll match it.',
          description: 'Body text for best price guarantee value proposition',
        }),
        icon: <LineUsdCircleIcon />,
        show: !!getStaticPagesByType(['bestPrice']).length,
        link: {
          text: intl.formatMessage({
            id: 'SPK48qvOl9',
            defaultMessage: 'Learn more',
            description: 'Hyperlink text to learn more about a feature on the homepage',
          }),
          href: '/best-price-guarantee',
        },
      },
      {
        key: 'atolProtected',
        heading: intl.formatMessage({
          id: 'I3fuNBoD5o',
          defaultMessage: 'ATOL protected',
          description: 'Section heading for ATOL protection value proposition',
        }),
        body: intl.formatMessage({
          id: 'z3/KspWOPX',
          defaultMessage: 'Book your trip with confidence! ATOL protection provided for eligible flights and flight inclusive holidays.',
          description: 'Body copy text for ATOL protection value proposition',
        }),
        icon: <LineCheckCircleIcon />,
        show: ATOL_REGIONS.includes(regionCode),
      },
      {
        key: 'customerService',
        heading: intl.formatMessage({
          id: 'flurzEXImO',
          defaultMessage: '24/7 Customer service',
          description: 'Heading for 24/7 customer service value proposition',
        }),
        body: intl.formatMessage({
          id: 'g8NIf64Ybp',
          defaultMessage: 'Our dedicated team is available online or on the phone anytime, whenever you need us.',
          description: 'Body text for 24/7 customer service value proposition',
        }),
        icon: <LinePhoneIcon />,
        show: !NO_24_7_SUPPORT_REGIONS.includes(regionCode),
      },
      {
        key: 'flexibleCancellation',
        heading: intl.formatMessage({
          id: 'IVVutfaP/m',
          defaultMessage: 'Flexible bookings',
          description: 'Section heading for flexible bookings value proposition',
        }),
        body: intl.formatMessage({
          id: 'tVQeZiCZlG',
          defaultMessage: 'Travel with peace of mind with our flexible hotel cancellation and date change policies.',
          description: 'Body copy text for flexible bookings value proposition',
        }),
        icon: <LineRefundIcon />,
        show: true,
      },
    ]
    return getValueSectionContent(currentRegionCode).filter(value => value.show)
  }, [currentRegionCode, intl])

  const headingText = intl.formatMessage({
    id: '7ztxy0ldQz',
    defaultMessage: 'Why you should book with us',
    description: 'Section heading for value proposition banner section',
  })

  return (
    <Pane>
      <StyledLayoutContainer>
        <VerticalSpacer gap={40}>
          <Heading variant="heading2">{headingText}</Heading>
          <ValuePropositionItems values={values} />
        </VerticalSpacer>
      </StyledLayoutContainer>
    </Pane>
  )
}

export default React.memo(ValuePropositionBanner)
